import React, { useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { NavLink } from "@mantine/core";
import {
    IconDashboard,
    IconFolder,
    IconChartBar,
    IconCategory,
    IconCloud,
} from "@tabler/icons-react";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { AdminPermissions } from "@config/permissions";
import type { AdminPermission } from "@config/permissions";
import "./AdminPage.scss";

const DRAWER_WIDTH = 240;

interface NavItem {
    label: string;
    path: string;
    icon: React.ReactNode;
    permissions: AdminPermission[];
}

const NAV_ITEMS: NavItem[] = [
    {
        label: "Dashboard",
        path: "/admin/dashboard",
        icon: <IconDashboard size={20} />,
        permissions: [AdminPermissions.DASHBOARD_VIEW],
    },
    {
        label: "Monitoring",
        path: "/admin/monitoring",
        icon: <IconChartBar size={20} />,
        permissions: [AdminPermissions.MONITORING_VIEW],
    },
    {
        label: "Document Types",
        path: "/admin/doctypes",
        icon: <IconFolder size={20} />,
        permissions: [
            AdminPermissions.DOCTYPE_VIEW,
            AdminPermissions.DOCTYPE_MANAGE,
        ],
    },
    {
        label: "Categories",
        path: "/admin/categories",
        icon: <IconCategory size={20} />,
        permissions: [
            AdminPermissions.CATEGORY_VIEW,
            AdminPermissions.CATEGORY_MANAGE,
        ],
    },
    {
        label: "Providers",
        path: "/admin/providers",
        icon: <IconCloud size={20} />,
        permissions: [
            AdminPermissions.PROVIDER_VIEW,
            AdminPermissions.PROVIDER_CREDENTIAL_VIEW,
            AdminPermissions.PROVIDER_MODEL_VIEW,
        ],
    },
];

const AdminPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { hasPermission } = useAdminPermissions();

    const visibleNavItems = useMemo(
        () => NAV_ITEMS.filter((item) => item.permissions.some(hasPermission)),
        [hasPermission],
    );

    return (
        <div className="admin-page">
            <nav
                className="admin-drawer"
                style={{ width: DRAWER_WIDTH, flexShrink: 0 }}
            >
                <div className="admin-nav-list">
                    {visibleNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            label={item.label}
                            leftSection={item.icon}
                            active={location.pathname.startsWith(item.path)}
                            onClick={() => navigate(item.path)}
                            className="admin-nav-item"
                        />
                    ))}
                </div>
            </nav>
            <main className="admin-content">
                <Outlet />
            </main>
        </div>
    );
};

export default AdminPage;
